/**
 * ADR-0216 — support-login redeem landing.
 *
 * Runs at `app.sill.so/support-login#token=<raw>` (the raw token is in
 * the URL FRAGMENT, never the query — fragments never reach access
 * logs / Referer headers). On mount it reads + scrubs the token, then
 * waits for an explicit "Continue" click before redeeming (security
 * review: opening the link alone never swaps this browser's session):
 *
 *   1. Read the token from `window.location.hash`.
 *   2. IMMEDIATELY clear the hash via `history.replaceState` so a
 *      subsequent bookmark / share of the tab doesn't leak the raw
 *      token.
 *   3. POST it to `${API_ORIGIN}/v1/auth/support-login`. The server
 *      responds 200 + Set-Cookie: sill_session=... (session cookie —
 *      no Max-Age), or 401 with a uniform reason.
 *   4. On success, redirect to `/` — the bootstrap `<AuthProvider>`
 *      re-runs `/v1/auth/me` and hydrates the session (with the
 *      support_session banner data folded onto the User).
 *   5. On failure, render "That support-login link has expired or was
 *      already used." plus a "Back to sign-in" fallback.
 *
 * `isAnonymousAuthPath` in `auth-context.tsx` gates `/support-login`
 * so the bootstrap's 401 (no cookie yet at first paint) doesn't yank
 * the visitor to the Login view before the POST lands.
 */

import { useEffect, useState } from 'react';

import { API_ORIGIN } from '../../app/env.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface SupportLoginProps {
  setView: (v: View) => void;
}

type PageState =
  | { kind: 'confirm'; token: string }
  | { kind: 'redeeming' }
  | { kind: 'error'; message: string }
  | { kind: 'success' };

export function SupportLogin({ setView }: SupportLoginProps): JSX.Element {
  const C = useC();
  const [state, setState] = useState<PageState>({ kind: 'redeeming' });

  // Redeem only on an explicit click (security review major 3): opening
  // the link alone must never swap the session in this browser.
  const redeem = async (token: string): Promise<void> => {
    setState({ kind: 'redeeming' });
    try {
      const res = await fetch(`${API_ORIGIN}/v1/auth/support-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (res.status === 200) {
        // Reload at the app root so the AuthProvider re-hydrates from
        // /v1/auth/me, which now carries support_session for the banner.
        setState({ kind: 'success' });
        window.location.assign('/');
        return;
      }
      setState({ kind: 'error', message: 'That support-login link has expired or was already used.' });
    } catch {
      setState({ kind: 'error', message: 'Could not reach the service. Try the link again in a moment.' });
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Read token from the fragment (`#token=<raw>`) and immediately
    // scrub the URL so a screen-share / accidental bookmark can't
    // leak the token. `history.replaceState` doesn't add a history
    // entry.
    const raw_hash = window.location.hash.replace(/^#/, '');
    let token = '';
    const parts = raw_hash.split('&');
    for (const part of parts) {
      const [k, v] = part.split('=');
      if (k === 'token' && typeof v === 'string' && v.length > 0) {
        token = decodeURIComponent(v);
        break;
      }
    }
    try {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    } catch {
      /* older browsers — ignore, the redeem still runs */
    }

    if (token === '') {
      setState({
        kind: 'error',
        message: 'Missing token in link.',
      });
      return;
    }

    setState({ kind: 'confirm', token });
  }, []);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-24 text-center">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 12,
        }}
      >
        SUPPORT LOGIN
      </div>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(32px, 6vw, 48px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -1.5,
          marginBottom: 16,
        }}
      >
        {state.kind === 'error' ? 'Try again.' : state.kind === 'confirm' ? 'Open support session?' : 'One moment.'}
      </h1>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 14,
          color: C.muted,
          lineHeight: 1.6,
        }}
      >
        {state.kind === 'error'
          ? state.message
          : state.kind === 'confirm'
            ? 'This opens a read-only Sill support view of a merchant account for up to 60 minutes. Any account signed in on this browser will be signed out. Only continue if you generated this link in ops.'
            : 'Opening a read-only view of the account…'}
      </p>
      {state.kind === 'confirm' ? (
        <button
          type="button"
          onClick={() => void redeem(state.token)}
          className="mt-8 px-5 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            border: 'none',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Continue
        </button>
      ) : null}
      {state.kind === 'error' ? (
        <button
          type="button"
          onClick={() => setView('login')}
          className="mt-8 px-5 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Back to sign-in
        </button>
      ) : null}
    </div>
  );
}
